import {
  BoundSelectField,
  BoundTextField,
  Button,
  FormHeading,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useComputed,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { useEffect } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  CostClassificationType,
  CostSubClassificationType,
  FeatureFlagType,
  SaveCostCodeInput,
} from "src/generated/graphqlTypes";
import { updateQuery } from "src/mutations/utils";
import {
  useCostCodeOverviewTabMetadataQuery,
  useSaveCostCodesMutation,
} from "src/routes/admin/costCodes/CostCodeOverviewTab.generated";
import { BoundMaterialLeadTimeConfigFields } from "../components/BoundMaterialLeadTimeConfigFields";
import { BoundOptionPricingConfigFields } from "../components/BoundOptionPricingConfigFields";
import {
  MaterialLeadTimeFormValue,
  OptionPricingFormValue,
  materialLeadTimeConfigConfig,
  maybeMaterialLeadTimeConfig,
  maybeOptionPricingConfig,
  optionPricingConfigConfig,
} from "../utils";

export function CreateNewCostCodeModal() {
  const { openModal } = useModal();
  const isOptionCatalogV2Enabled = useFeatureFlag(FeatureFlagType.OptionsCatalogV2);

  return (
    <Button
      label="Create cost code"
      onClick={() =>
        openModal({
          content: <CreateNewCostCodeModalForm isOptionCatalogV2Enabled={isOptionCatalogV2Enabled} />,
          size: "lg",
        })
      }
    />
  );
}

function CreateNewCostCodeModalForm({ isOptionCatalogV2Enabled }: { isOptionCatalogV2Enabled: boolean }) {
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const { data } = useCostCodeOverviewTabMetadataQuery();
  const [saveCostCodes] = useSaveCostCodesMutation();
  const { costDivisions = [], enumDetails } = data || {};
  const { costClassificationType, costSubClassificationType } = enumDetails ?? {};
  const formState = useFormState({ config: formConfig });
  const { valid, subClassification, showSubClassification } = useComputed(
    () => ({
      valid: formState.valid,
      showSubClassification: formState.costClassification.value === CostClassificationType.Hard,
      subClassification: formState.costSubClassification.value,
    }),
    [formState],
  );

  useEffect(() => {
    if (!showSubClassification) {
      formState.costSubClassification.set(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubClassification]);

  async function onSubmit() {
    const { optionPricingConfig, materialLeadTimeConfig, ...others } = formState.value;
    await saveCostCodes({
      variables: {
        input: {
          costCodes: [
            {
              ...others,
              ...maybeOptionPricingConfig({ ...optionPricingConfig }),
              ...maybeMaterialLeadTimeConfig({ ...materialLeadTimeConfig }),
            },
          ],
        },
      },
      update: updateQuery((data) => data.saveCostCodes.costCodes, "costCodes"),
    });

    closeModal();
    triggerNotice({ message: "Cost code created successfully", icon: "success" });
  }

  return (
    <>
      <ModalHeader>Create cost code</ModalHeader>
      <ModalBody>
        <FormLines>
          <FormHeading title="Details" />
          <BoundTextField label="Name" field={formState.name} />
          <BoundTextField label="Number" field={formState.number} />
          <BoundSelectField
            label="Division"
            field={formState.divisionId}
            options={costDivisions}
            getOptionLabel={({ name, version }) => `${name} V${version}`}
          />
          <BoundSelectField
            label="Classification"
            field={formState.costClassification}
            options={costClassificationType ?? []}
            getOptionLabel={(costClass) => costClass.name}
            getOptionValue={(costClass) => costClass.code as CostClassificationType}
          />
          {showSubClassification && (
            <BoundSelectField
              required
              label="Sub Classification"
              field={formState.costSubClassification}
              options={costSubClassificationType ?? []}
              getOptionLabel={(costClass) => costClass.name}
              getOptionValue={(costClass) => costClass.code as CostSubClassificationType}
            />
          )}
          {isOptionCatalogV2Enabled && (
            <>
              <FormHeading title="Pricing" />
              <BoundOptionPricingConfigFields
                postCutoffDesiredMarginInBasisPoints={
                  formState.optionPricingConfig.postCutoffDesiredMarginInBasisPoints
                }
                preCutoffDesiredMarginInBasisPoints={formState.optionPricingConfig.preCutoffDesiredMarginInBasisPoints}
                selectionFeeInCents={formState.optionPricingConfig.selectionFeeInCents}
                minimumPriceInCents={formState.optionPricingConfig.minimumPriceInCents}
                reasonNote={formState.optionPricingConfig.reasonNote}
              />
              <FormHeading title="Material Lead Times" />
              <BoundMaterialLeadTimeConfigFields
                minLeadTimeInDays={formState.materialLeadTimeConfig.minLeadTimeInDays}
                expectedLeadTimeInDays={formState.materialLeadTimeConfig.expectedLeadTimeInDays}
                maxLeadTimeInDays={formState.materialLeadTimeConfig.maxLeadTimeInDays}
                reasonNote={formState.materialLeadTimeConfig.reasonNote}
              />
            </>
          )}
        </FormLines>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={closeModal} />
        <Button
          label="Create cost code"
          data-testid="createCostCodeBtn"
          disabled={!valid || (showSubClassification && !subClassification)}
          onClick={onSubmit}
        />
      </ModalFooter>
    </>
  );
}

type FormValue = Omit<SaveCostCodeInput, "optionPricingConfig" | "materialLeadTimeConfig"> & {
  materialLeadTimeConfig: MaterialLeadTimeFormValue;
  optionPricingConfig: OptionPricingFormValue;
};

const formConfig: ObjectConfig<FormValue> = {
  name: { type: "value", rules: [required] },
  number: { type: "value", rules: [required] },
  divisionId: { type: "value", rules: [required] },
  costClassification: { type: "value", rules: [required] },
  costSubClassification: { type: "value" },
  optionPricingConfig: { type: "object", config: optionPricingConfigConfig },
  materialLeadTimeConfig: { type: "object", config: materialLeadTimeConfigConfig },
};
